import { Box, Button } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { useParams } from "react-router-dom";
import { useAuthGate } from "../../../hooks/useAuthGate";
import { usePanelTokens } from "../../../utils/panelTokens";
import { DOWNLOADS_PUBLIC_PATH, findDownload } from "./catalog";
import { Link as RouterLink } from "react-router-dom";

/**
 * Public detail page for one catalog entry. The page is readable logged out;
 * the CTA is what gates — `access: "signin"` entries bounce through the
 * accounts login and come back here.
 */
const PublicDownloadDetail = () => {
  const { slug } = useParams();
  const t = usePanelTokens();
  const { isAuthenticated, requireSignin } = useAuthGate();
  const item = findDownload(slug);

  if (!item) {
    return (
      <Box sx={{ background: t.ground, color: t.text, minHeight: "100%", px: 3, py: 10, textAlign: "center" }}>
        <Box sx={{ fontSize: 20, fontWeight: 700, color: t.textStrong, mb: 1 }}>Download not found</Box>
        <Box sx={{ fontSize: 14, color: t.textMuted, mb: 3 }}>This download has moved or no longer exists.</Box>
        <Button
          component={RouterLink}
          to={DOWNLOADS_PUBLIC_PATH}
          variant="contained"
          sx={{ backgroundColor: t.accent, color: t.onAccent, textTransform: "none", fontWeight: 600, borderRadius: 2 }}
        >
          All downloads
        </Button>
      </Box>
    );
  }

  const gated = item.access === "signin" && !isAuthenticated;
  const go = () => requireSignin(() => window.location.assign(item.action.href));

  return (
    <Box sx={{ background: t.ground, color: t.text, minHeight: "100%" }}>
      <Box sx={{ px: { xs: 3, md: 7 }, py: { xs: 4, md: 6 }, maxWidth: 900, mx: "auto" }}>
        <Box
          component={RouterLink}
          to={DOWNLOADS_PUBLIC_PATH}
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mb: 3, fontSize: 13, color: t.textMuted, textDecoration: "none", "&:hover": { color: t.accent } }}
        >
          <ArrowBackIcon sx={{ fontSize: 16 }} />
          All downloads
        </Box>

        <Box sx={{ fontSize: 11, letterSpacing: ".12em", color: t.accent, fontWeight: 700, mb: 1 }}>
          {item.category.toUpperCase()}
        </Box>
        <Box component="h1" sx={{ m: 0, fontSize: { xs: 28, md: 38 }, fontWeight: 700, letterSpacing: "-0.01em", color: t.textStrong, lineHeight: 1.2 }}>
          {item.title}
        </Box>
        <Box component="p" sx={{ mt: 2, mb: 0, fontSize: { xs: 15, md: 17 }, color: t.textMuted, lineHeight: 1.6 }}>
          {item.tagline}
        </Box>

        <Box sx={{ mt: 3.5, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <Button
            onClick={go}
            variant="contained"
            startIcon={<FileDownloadOutlinedIcon />}
            sx={{ backgroundColor: t.accent, color: t.onAccent, textTransform: "none", fontWeight: 700, borderRadius: 2, px: 3, py: 1.1, "&:hover": { backgroundColor: t.accent, filter: "brightness(0.95)" } }}
          >
            {gated ? `Sign in to ${item.action.label.toLowerCase()}` : item.action.label}
          </Button>
          {gated && (
            <Box sx={{ fontSize: 13, color: t.textFaint }}>
              Free developer account — you'll come back here afterwards.
            </Box>
          )}
        </Box>

        {/* Spec strip */}
        <Box sx={{ mt: 5, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 0, border: `1px solid ${t.border}`, borderRadius: 2, overflow: "hidden" }}>
          {item.meta.map((m) => (
            <Box key={m.label} sx={{ p: 2, background: t.surface, borderRight: `1px solid ${t.border}` }}>
              <Box sx={{ fontSize: 11, letterSpacing: ".1em", color: t.textFaint, mb: 0.6 }}>{m.label.toUpperCase()}</Box>
              <Box sx={{ fontSize: 13.5, color: t.text }}>{m.value}</Box>
            </Box>
          ))}
        </Box>

        {item.description.map((p) => (
          <Box key={p} component="p" sx={{ mt: 3, mb: 0, fontSize: 14.5, color: t.text, lineHeight: 1.75 }}>
            {p}
          </Box>
        ))}

        <Box sx={{ mt: 5, display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 4 }}>
          <Box>
            <Box sx={{ fontSize: 11, letterSpacing: ".14em", color: t.textMuted, mb: 2 }}>WHAT YOU GET</Box>
            {item.includes.map((line) => (
              <Box key={line} sx={{ display: "flex", gap: 1, mb: 1.2, fontSize: 13.5, color: t.text, lineHeight: 1.6 }}>
                <CheckIcon sx={{ fontSize: 16, color: t.success, mt: 0.3, flexShrink: 0 }} />
                {line}
              </Box>
            ))}
          </Box>
          <Box>
            <Box sx={{ fontSize: 11, letterSpacing: ".14em", color: t.textMuted, mb: 2 }}>REQUIREMENTS</Box>
            {item.requirements.map((line) => (
              <Box key={line} sx={{ mb: 1.2, fontSize: 13.5, color: t.textMuted, lineHeight: 1.6 }}>
                {line}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PublicDownloadDetail;
